/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
// ANCHOR Payloads
import { formatHasuraDate } from '../extras/formatHasuraDate';
import {
  ICreateProviderForms,
  ICreateQuestion,
  ICreateQuestionAnswer,
  IBaseFormData,
  IFetchProviderForms,
  IUpdateProviderForms,
  IUpdateQuestion,
  IFetchFormData,
  IUpdateQuestionPosition,
  ICreateFormSubmission,
  IGetFormSubmission,
  ICreateFormQuestionSubmission,
  IUpdateSubmissionData,
} from './payloads/forms';

// ANCHOR Results
import {
  IFormQuestionAnswersResult, IFormQuestionsResult,
  IFormsResult, IFormSubmissionDetails, IFormSubmissionQuestions, IFormSubmissionsResult,
  IProviderFormsResult,
} from './results/forms';

// SECTION FORMS
// ANCHOR Get all forms by provider
export async function getProviderForms(
  { token, providerId }: IFetchProviderForms,
): Promise<IProviderFormsResult[]> {
  const { sdk } = await import('../axios/graphql');
  const { forms } = await sdk(token).GetProviderForms({
    id: providerId,
  });

  return forms.map((form) => ({
    ...form,
    description: form.description ?? '',
  }));
}

// ANCHOR Get a form by id
export async function getFormById(
  { id }: IFetchFormData,
): Promise<IFormsResult | undefined> {
  const { sdk } = await import('../axios/graphql');
  const { form } = await sdk().GetFormById({
    id,
  });

  if (form) {
    return form;
  }

  return undefined;
}

interface IFormQuestionSubmission {
  readonly id: string;
  readonly questionId: string;
  readonly userId: string;
}

// ANCHOR Get a form question submission
export async function getFormQuestionSubmission(
  { id, questionId, userId }: IFormQuestionSubmission,
) {
  const { sdk } = await import('../axios/graphql');
  const { formQuestionSubmissions } = await sdk().GetFormQuestionSubmission({
    submission_id: id,
    question_id: questionId,
    user_id: userId,
  });

  if (formQuestionSubmissions) {
    return formQuestionSubmissions;
  }

  return undefined;
}

// ANCHOR Create a form
export async function createForm(
  { token, providerId, ...payload }: ICreateProviderForms,
) {
  const { sdk } = await import('../axios/graphql');
  const { form } = await sdk(token).CreateForm({
    providerId,
    description: `A ${String(payload.type)} generated at ${new Date().toDateString()}`,
    ...payload,
  });

  return form;
}

// ANCHOR Update a form
export async function updateFormById(
  { token, ...payload }: IUpdateProviderForms,
) {
  const { sdk } = await import('../axios/graphql');
  const { form } = await sdk(token).UpdateFormById({
    ...payload,
  });

  return form;
}

// ANCHOR Delete a form
export async function deleteFormById(
  { token, id }: IBaseFormData,
) {
  const { sdk } = await import('../axios/graphql');
  const { form } = await sdk(token).DeleteFormById({
    id,
  });

  return form;
}

// SECTION QUESTIONS
// ANCHOR Create a question
export async function createFormQuestion(
  { token, ...payload }: ICreateQuestion,
) {
  const { sdk } = await import('../axios/graphql');

  const isOpenEnded = payload.type === 'essay';
  const caption = payload.type === 'spacer'
    ? payload.name
    : null;

  const { formQuestion } = await sdk(token).CreateFormQuestion({
    ...payload,
    isOpenEnded,
    caption,
  });

  return formQuestion;
}

// ANCHOR Get a all questions for form
export async function getFormQuestions(
  { id }: IFetchFormData,
) {
  const { sdk } = await import('../axios/graphql');
  const { form } = await sdk().GetFormQuestions({
    id,
  });

  if (form) {
    return {
      shuffleQuestion: form.shuffleQuestion,
      questions: form.questions as IFormQuestionsResult[],
    };
  }

  return undefined;
}

// ANCHOR Get a form question by id
export async function getFormQuestionById(
  { id }: IFetchFormData,
): Promise<IFormQuestionsResult | undefined> {
  const { sdk } = await import('../axios/graphql');
  const { formQuestion } = await sdk().GetFormQuestionById({
    id,
  });

  if (formQuestion) {
    return formQuestion;
  }

  return undefined;
}

// ANCHOR Update a question
export async function updateFormQuestionById(
  { token, ...payload }: IUpdateQuestion,
) {
  const { sdk } = await import('../axios/graphql');
  const { formQuestion } = await sdk(token).UpdateFormQuestionById({
    ...payload,
  });

  return formQuestion;
}

// ANCHOR Update a question's position
export async function updateFormQuestionPositionById(
  { token, ...payload }: IUpdateQuestionPosition,
) {
  const { sdk } = await import('../axios/graphql');
  const { formQuestion } = await sdk(token).UpdateFormQuestionPositionById({
    ...payload,
  });

  return formQuestion;
}

// ANCHOR Delete a form question
export async function deleteFormQuestionById(
  { token, id }: IBaseFormData,
) {
  const { sdk } = await import('../axios/graphql');
  const { formQuestion } = await sdk(token).DeleteFormQuestionById({
    id,
  });

  return formQuestion;
}

// SECTION ANSWERS
// ANCHOR Create a form question answer
export async function createFormQuestionAnswer(
  { token, ...payload }: ICreateQuestionAnswer,
) {
  const { sdk } = await import('../axios/graphql');
  const { formQuestionAnswer } = await sdk(token).CreateFormQuestionAnswer({
    ...payload,
  });

  return formQuestionAnswer;
}

// ANCHOR Get all answers for question
export async function getFormQuestionAnswers(
  { id }: IFetchFormData,
): Promise<IFormQuestionAnswersResult[] | undefined> {
  const { sdk } = await import('../axios/graphql');
  const { question } = await sdk().GetFormQuestionAnswers({
    id,
  });

  if (question) {
    return question.answers as IFormQuestionAnswersResult[];
  }

  return undefined;
}

// ANCHOR Get all answers of a question for a student
export async function getFormQuestionAnswersForStudent(
  { id }: IFetchFormData,
): Promise<IFormQuestionAnswersResult[] | undefined> {
  const { sdk } = await import('../axios/graphql');
  const { question } = await sdk().GetFormQuestionAnswersForStudents({
    id,
  });

  if (question) {
    return question.answers as IFormQuestionAnswersResult[];
  }

  return undefined;
}

// ANCHOR Delete a form question answer
export async function deleteFormQuestionAnswerById(
  { token, id }: IBaseFormData,
) {
  const { sdk } = await import('../axios/graphql');
  const { formQuestionAnswer } = await sdk(token).DeleteFormQuestionAnswerById({
    id,
  });

  return formQuestionAnswer;
}

// SECTION: FORM SUBMISSIONS
// ANCHOR Create a form submission
export async function createFormSubmission(
  { token, ...payload }: ICreateFormSubmission,
) {
  const { sdk } = await import('../axios/graphql');
  const { formSubmission } = await sdk(token).CreateFormSubmission({
    ...payload,
  });

  return formSubmission;
}

// ANCHOR Get user's form submission
export async function getFormSubmissionByUser(
  { token, courseFormId, userId }: IGetFormSubmission,
) {
  const { sdk } = await import('../axios/graphql');
  const { formSubmission } = await sdk(token).GetFormSubmissions({
    courseFormId,
    userId,
  });

  return formSubmission as Partial<IFormSubmissionsResult[]>;
}

// ANCHOR Get form submission's summary of details
export async function getFormSubmissionDetails(
  { id }: IFetchFormData,
) {
  const { sdk } = await import('../axios/graphql');
  const { formSubmission } = await sdk().GetFormSubmissionDetails({
    id,
  });

  if (formSubmission) {
    const {
      attempt, finishedAt, startedAt, form,
      formQuestionSubmissionsAggregate,
    } = formSubmission;

    const submissionAggregate = formQuestionSubmissionsAggregate.aggregate?.sum;
    const questionAggregate = form.formQuestionAggregate.aggregate?.sum;

    return {
      id,
      attempt,
      finishedAt: formatHasuraDate(finishedAt),
      startedAt: formatHasuraDate(startedAt),
      initialTotalScore: submissionAggregate?.scoreBeforeRegrade,
      finalTotalScore: submissionAggregate?.finalScore,
      totalPossiblePoints: questionAggregate?.pointsPossible,
    } as IFormSubmissionDetails;
  }

  return undefined;
}

// ANCHOR Update form submission's finished time
export async function updateFormSubmissionFinishedAt(
  { id, status }: IUpdateSubmissionData,
) {
  const { sdk } = await import('../axios/graphql');

  const finishedAt = new Date().toISOString();

  const { formSubmission } = await sdk().UpdateFormSubmissionFinish({
    finishedAt,
    id,
    status,
  });

  return formSubmission;
}

// SECTION: FORM QUESTION SUBMISSIONS
// ANCHOR Create a form question submission
export async function createFormQuestionSubmission(
  { submissions }: ICreateFormQuestionSubmission,
) {
  const { sdk } = await import('../axios/graphql');
  const { formQuestionSubmission } = await sdk().CreateFormQuestionSubmission({
    objects: submissions,
  });

  return formQuestionSubmission;
}

export async function createFormQuestionSubmissions(
  { submissions }: ICreateFormQuestionSubmission,
) {
  const { sdk } = await import('../axios/graphql');
  const { formQuestionSubmission } = await sdk().CreateFormQuestionSubmission({
    objects: submissions,
  });

  return formQuestionSubmission;
}

// ANCHOR Update form question submissions
export async function updateFormQuestionSubmissions(
  { submissions }: ICreateFormQuestionSubmission,
) {
  const { sdk } = await import('../axios/graphql');
  const { formQuestionSubmission } = await sdk().UpdateFormQuestionSubmission({
    objects: submissions,
  });

  return formQuestionSubmission;
}

// ANCHOR Get form submission's summary of details
export async function getFormSubmissionQuestions(
  { id }: IFetchFormData,
) {
  const { sdk } = await import('../axios/graphql');
  const { formSubmission } = await sdk().GetFormSubmissionQuestions({
    id,
  });

  if (formSubmission) {
    const { formQuestionSubmission } = formSubmission;

    return formQuestionSubmission.map((submission) => {
      const {
        answers, state, scoreBeforeRegrade, finalScore, formQuestion, remark,
      } = submission;
      const {
        name, type, position, pointsPossible,
      } = formQuestion;

      return {
        id: submission.id,
        answers,
        state,
        scoreBeforeRegrade,
        finalScore,
        remark,
        question: {
          id: formQuestion.id,
          name,
          type,
          position,
          pointsPossible,
        },
      } as IFormSubmissionQuestions;
    });
  }

  return undefined;
}
